import { useEffect } from 'react'
import { appToast } from '../../../components/AppToast'
import AppFormFooterBar from '../../../components/AppFormFooterBar'
import { useTestSettingsScreen } from './hooks/useTestSettingsScreen'

function PillToggle({ name, value, onChange, options }) {
  return (
    <div className="inline-flex items-center rounded-xl border border-outline-variant/30 bg-surface-container-low p-1 gap-0.5">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange({ target: { name, value: opt.value } })}
          className={`relative rounded-lg px-4 py-2 text-xs font-bold transition-all duration-200 select-none cursor-pointer ${
            value === opt.value
              ? 'bg-primary text-on-primary shadow-sm'
              : 'text-on-surface-variant hover:text-on-surface hover:bg-surface-container-highest/50'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}

function PillCheckboxes({ items }) {
  return (
    <div className="inline-flex items-center rounded-xl border border-outline-variant/30 bg-surface-container-low p-1 gap-0.5">
      {items.map((item) => (
        <button
          key={item.key}
          type="button"
          onClick={item.onChange}
          className={`relative rounded-lg px-4 py-2 text-xs font-bold transition-all duration-200 select-none cursor-pointer ${
            item.checked
              ? 'bg-primary text-on-primary shadow-sm'
              : 'text-on-surface-variant hover:text-on-surface hover:bg-surface-container-highest/50'
          }`}
        >
          {item.label}
        </button>
      ))}
    </div>
  )
}

function SettingRow({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-4 py-3.5">
      <label className="text-sm font-semibold text-on-surface shrink-0">{label}</label>
      {children}
    </div>
  )
}

function SectionCard({ title, icon, children }) {
  return (
    <div className="rounded-3xl border border-outline-variant/20 bg-surface-container-lowest overflow-hidden shadow-sm">
      <div className="flex items-center gap-2.5 px-6 py-4 border-b border-outline-variant/15 bg-surface-container-low/40">
        <span className="material-symbols-outlined text-primary text-lg">{icon}</span>
        <h3 className="text-sm font-bold text-on-surface tracking-tight">{title}</h3>
      </div>
      <div className="divide-y divide-outline-variant/10 px-6">
        {children}
      </div>
    </div>
  )
}

export default function TestSettingsScreen() {
  const { state, actions } = useTestSettingsScreen()

  useEffect(() => {
    if (state.error) appToast.error(state.error)
  }, [state.error])

  useEffect(() => {
    if (state.success) appToast.success(state.success)
  }, [state.success])

  const { formData } = state

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-24">
      {/* ── Page Header (Rule 87: Canopy Hierarchy - No arrow_back) ── */}
      <section className="flex flex-col gap-1 border-b border-outline-variant/10 pb-5">
        <h1 className="font-display text-4xl font-extrabold leading-[0.98] tracking-[-0.02em] text-on-surface">
          Test Settings
        </h1>
        <p className="mt-1 text-xs font-medium text-on-surface-variant">
          Configure default test marks population mode, automatic notifications, and report inclusion rules.
        </p>
      </section>

      {state.isLoading ? (
        <div className="rounded-3xl border border-outline-variant/25 bg-surface-container-lowest p-12 text-center text-sm text-on-surface-variant font-medium">
          Loading test settings...
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* ── Left Column: Form ── */}
          <div className="lg:col-span-7 flex flex-col gap-5">
            {/* Marks Allocation */}
            <SectionCard title="Marks Allocation" icon="grading">
              <SettingRow label="Populate Marks">
                <PillToggle
                  name="populateMarks"
                  value={formData.populateMarks}
                  onChange={actions.handleChange}
                  options={[
                    { value: 'A', label: 'All Students' },
                    { value: 'S', label: 'Selected Subjects' },
                  ]}
                />
              </SettingRow>
            </SectionCard>

            {/* Notification Recipients */}
            <SectionCard title="Notification Recipients" icon="notifications_active">
              <SettingRow label="SMS To">
                <PillCheckboxes
                  items={[
                    { key: 'smsStudents', label: 'Students', checked: formData.smsToStudents, onChange: () => actions.toggleCheckbox('smsToStudents') },
                    { key: 'smsFather', label: 'Father', checked: formData.smsToFather, onChange: () => actions.toggleCheckbox('smsToFather') },
                    { key: 'smsMother', label: 'Mother', checked: formData.smsToMother, onChange: () => actions.toggleCheckbox('smsToMother') },
                  ]}
                />
              </SettingRow>
              <SettingRow label="WhatsApp To">
                <PillCheckboxes
                  items={[
                    { key: 'waStudents', label: 'Students', checked: formData.whatsappToStudents, onChange: () => actions.toggleCheckbox('whatsappToStudents') },
                    { key: 'waFather', label: 'Father', checked: formData.whatsappToFather, onChange: () => actions.toggleCheckbox('whatsappToFather') },
                    { key: 'waMother', label: 'Mother', checked: formData.whatsappToMother, onChange: () => actions.toggleCheckbox('whatsappToMother') },
                  ]}
                />
              </SettingRow>
            </SectionCard>

            {/* Dispatch Automation */}
            <SectionCard title="Dispatch Automation" icon="send">
              <SettingRow label="SMS Test Schedule">
                <PillToggle
                  name="smsSchedule"
                  value={formData.smsSchedule}
                  onChange={actions.handleChange}
                  options={[
                    { value: 'M', label: 'Manual' },
                    { value: 'A', label: 'Automatic' },
                  ]}
                />
              </SettingRow>
              <SettingRow label="WhatsApp Test Schedule">
                <PillToggle
                  name="whatsappSchedule"
                  value={formData.whatsappSchedule}
                  onChange={actions.handleChange}
                  options={[
                    { value: 'M', label: 'Manual' },
                    { value: 'A', label: 'Automatic' },
                  ]}
                />
              </SettingRow>
              <SettingRow label="SMS Marks">
                <PillToggle
                  name="smsMarks"
                  value={formData.smsMarks}
                  onChange={actions.handleChange}
                  options={[
                    { value: 'M', label: 'Manual' },
                    { value: 'A', label: 'Automatic' },
                  ]}
                />
              </SettingRow>
              <SettingRow label="WhatsApp Marks">
                <PillToggle
                  name="whatsappMarks"
                  value={formData.whatsappMarks}
                  onChange={actions.handleChange}
                  options={[
                    { value: 'M', label: 'Manual' },
                    { value: 'A', label: 'Automatic' },
                  ]}
                />
              </SettingRow>
            </SectionCard>

            {/* Report Indicators */}
            <SectionCard title="Report Indicators" icon="leaderboard">
              <SettingRow label="Highest Marks">
                <PillToggle
                  name="highestMarks"
                  value={formData.highestMarks}
                  onChange={actions.handleChange}
                  options={[
                    { value: 'I', label: 'Include' },
                    { value: 'E', label: 'Exclude' },
                  ]}
                />
              </SettingRow>
              <SettingRow label="Rank">
                <PillToggle
                  name="rank"
                  value={formData.rank}
                  onChange={actions.handleChange}
                  options={[
                    { value: 'I', label: 'Include' },
                    { value: 'E', label: 'Exclude' },
                  ]}
                />
              </SettingRow>
            </SectionCard>

            {/* Error Message */}
            {state.error && (
              <div className="rounded-2xl border border-error/30 bg-error-container p-4 text-xs font-medium text-error">
                {state.error}
              </div>
            )}
          </div>

          {/* ── Right Column: TIPS Panel ── */}
          <div className="lg:col-span-5">
            <section className="rounded-3xl border border-outline-variant/20 bg-slate-50/80 p-6 sticky top-6">
              <div className="flex items-center gap-2.5 mb-5 pb-4 border-b border-outline-variant/20">
                <span className="material-symbols-outlined text-amber-500 text-xl">lightbulb</span>
                <h2 className="text-base font-bold text-on-surface">Tips & Guidelines</h2>
              </div>
              <ul className="space-y-4 text-xs text-on-surface-variant leading-relaxed">
                <li className="flex gap-2.5">
                  <span className="material-symbols-outlined text-primary/70 text-base mt-0.5 shrink-0">info</span>
                  <div>
                    <span className="text-on-surface font-bold">Populate Marks:</span>{' '}
                    "Selected Subjects" will filter out students who have not opted for the selected subject while entering marks, whereas "All Students" will show all students irrespective of subjects.
                  </div>
                </li>
                <li className="flex gap-2.5">
                  <span className="material-symbols-outlined text-primary/70 text-base mt-0.5 shrink-0">info</span>
                  <div>
                    <span className="text-on-surface font-bold">SMS / WhatsApp To:</span>{' '}
                    This setting controls default recipient checkboxes when creating or broadcasting test notifications to students, father, or mother.
                  </div>
                </li>
                <li className="flex gap-2.5">
                  <span className="material-symbols-outlined text-primary/70 text-base mt-0.5 shrink-0">info</span>
                  <div>
                    <span className="text-on-surface font-bold">Schedule Automation:</span>{' '}
                    "Automatic" will queue notifications immediately upon test creation, whereas "Manual" awaits manual trigger from test row actions.
                  </div>
                </li>
                <li className="flex gap-2.5">
                  <span className="material-symbols-outlined text-primary/70 text-base mt-0.5 shrink-0">info</span>
                  <div>
                    <span className="text-on-surface font-bold">Highest Marks & Rank:</span>{' '}
                    Controls whether comparative metrics (highest test score and rank in batch) are appended to report cards and SMS messages.
                  </div>
                </li>
              </ul>
            </section>
          </div>
        </div>
      )}

      {/* ── Shared Standard Form Footer ── */}
      <AppFormFooterBar
        onSaveExit={actions.handleSave}
        saveLabel="Save Settings"
        isSaving={state.isSaving}
      />
    </div>
  )
}
